import { Box, HStack, VStack } from '@betterment/samba-react';

import { PortfolioAllocationRow } from './components/PortfolioAllocationRow';
import { PortfolioDescriptionAndDisclosure, PortfolioStandaloneDisclosure } from './components/PortfolioDisclosures';
import { PortfolioMessages } from './components/PortfolioMessages';
import { PortfolioStrategyRow } from './components/PortfolioStrategyRow';
import { PortfolioStrategyStats } from './components/PortfolioStrategyStats';
import { RiskLevelBasedPortfolio } from './data';

import { MessageFragment } from '../../../../graphql/fragments/message.generated';
import { ThemedImage } from '../../../../Shared/components-v2/themed-image/ThemedImage';

type Props = {
  portfolio: RiskLevelBasedPortfolio;
  legacyGoalId: number;
  hasEverBeenFunded: boolean;
  messages: MessageFragment[];
};

export const RiskLevelBasedPortfolioStrategy: React.FC<Props> = ({
  portfolio,
  legacyGoalId,
  hasEverBeenFunded,
  messages,
}) => {
  const {
    disclosureUrl,
    hasPendingPortfolioChangeRequest,
    isStrategySelectionEligible,
    isAllocationEditable,
    riskLevel,
    strategy,
  } = portfolio;
  const { name: strategyName, descriptionHtml, icon } = strategy;
  const { name: riskLevelName } = riskLevel;

  const shouldShowAllocation = riskLevelName && isAllocationEditable;

  return (
    <VStack css={{ gap: 'space.300' }}>
      <HStack css={{ gap: 'space.200', alignItems: 'center' }}>
        {icon && (
          <Box css={{ width: '96px', height: '96px' }}>
            <ThemedImage themedAsset={icon} alt={strategyName} width="96px" height="96px" />
          </Box>
        )}
        <VStack css={{ gap: 'space.150' }}>
          <PortfolioStrategyRow
            legacyGoalId={legacyGoalId}
            name={strategyName}
            isEditable={isStrategySelectionEligible}
            hasPendingPortfolioChangeRequest={hasPendingPortfolioChangeRequest}
          />
          {shouldShowAllocation && (
            <PortfolioAllocationRow legacyGoalId={legacyGoalId} allocationDescription={riskLevelName} />
          )}
        </VStack>
      </HStack>
      {descriptionHtml && (
        <PortfolioDescriptionAndDisclosure descriptionHtml={descriptionHtml} disclosureUrl={disclosureUrl} />
      )}
      <PortfolioMessages messages={messages} />
      <PortfolioStrategyStats portfolio={portfolio} hasEverBeenFunded={hasEverBeenFunded} />
      {!descriptionHtml && disclosureUrl && <PortfolioStandaloneDisclosure disclosureUrl={disclosureUrl} />}
    </VStack>
  );
};
